/** @jsx jsx */
import React, { type HTMLAttributes } from 'react';
import { css as css2, jsx } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import DownChevron from '@atlaskit/icon/utility/migration/chevron-down--hipchat-chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import type { CmdbNumericStatusCategory } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/status.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';

export type Status = {
	id: string;
	name: string;
	category: CmdbNumericStatusCategory | null;
};

export const StatusLozenge = ({
	isEditable,
	status,
	ellipsis,
}: {
	isEditable: boolean;
	status: Status;
	ellipsis?: boolean;
}) => {
	const { name, category } = status;
	return (
		<LozengeOverrides
			// @ts-expect-error - TS2322: Type 'null' is not assignable to type '0 | 1 | 2 | null'.
			category={category}
			isEditable={isEditable}
			data-testid="servicedesk-insight-attribute-views.ui.status.status-lozenge"
		>
			<Lozenge>
				<LozengeContent>
					{isEditable || ellipsis ? <EllipsisOverflow>{name}</EllipsisOverflow> : name}
					{isEditable && <DownChevron LEGACY_size="small" spacing="compact" label="" />}
				</LozengeContent>
			</Lozenge>
		</LozengeOverrides>
	);
};

const statusCategory = {
	'0': 'inactive',
	'1': 'active',
	'2': 'pending',
	null: 'none',
} as const;

const statusBackground = {
	inactive: token('color.background.danger'),
	active: token('color.background.success'),
	pending: token('color.background.warning'),
	none: token('color.background.neutral'),
} as const;

const bgHoverColor = {
	inactive: token('color.background.danger.hovered'),
	active: token('color.background.success.hovered'),
	pending: token('color.background.warning.hovered'),
	none: token('color.background.neutral.hovered'),
} as const;

const textColor = {
	inactive: token('color.text.danger'),
	active: token('color.text.success'),
	pending: token('color.text'),
	none: token('color.text.subtle'),
} as const;

type LozengeOverridesType = {
	isEditable?: boolean;
	category: keyof typeof statusCategory;
};

// TODO remove eslint-disable once we have migrated to the new css prop
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const lozengeSpanEditablePaddingStyle = css2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		padding: '4px 6px',
	},
});

// TODO: remove this once we have migrated to the new css prop
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const lozengeSpanNonEditablePaddingStyle = css2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		padding: '2px',
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const lozengeOverridesStyles = css2({
	display: 'flex',
	alignItems: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		verticalAlign: 'text-bottom',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span > span': {
		textOverflow: 'clip',
		whiteSpace: 'normal',
	},
});

// TODO remove eslint-disable once we have migrated to the new css prop
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const lozengeContentStyles = css2({
	display: 'flex',
	alignItems: 'center',
});

const LozengeContent = ({ children }: { children: React.ReactNode }) => (
	<div css={lozengeContentStyles}>{children}</div>
);

// TODO remove eslint-disable once we have migrated to the new css prop
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const ellipsisOverflowStyles = css2({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const EllipsisOverflow = ({ children }: { children: React.ReactNode }) => (
	<div css={ellipsisOverflowStyles}>{children}</div>
);

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const categoryStyles = css2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		backgroundColor: 'var(--status-background-color)',
		color: 'var(--status-text-color)',
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const editableCategoryStyles = css2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> span': {
		'&:hover': {
			backgroundColor: 'var(--status-background-hover-color)',
		},
	},
});
const LozengeOverridesNew = ({
	children,
	...props
}: HTMLAttributes<HTMLDivElement> & LozengeOverridesType) => (
	<div
		css={[
			lozengeOverridesStyles,
			props.isEditable ? lozengeSpanEditablePaddingStyle : lozengeSpanNonEditablePaddingStyle,
			categoryStyles,
			props.isEditable && editableCategoryStyles,
		]}
		{...props}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			{
				'--status-background-hover-color': bgHoverColor[statusCategory[props.category]],
				'--status-background-color': statusBackground[statusCategory[props.category]],
				'--status-text-color': textColor[statusCategory[props.category]],
			} as React.CSSProperties
		}
	>
		{children}
	</div>
);

// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LozengeOverridesOld = styled.div<LozengeOverridesType>`
	display: flex;
	align-items: center;

	> span {
		padding: ${(props) => (props.isEditable ? '4px 6px' : '2px')};
		vertical-align: text-bottom;
		color: ${(props) => textColor[statusCategory[props.category]]};
		background-color: ${(props) => statusBackground[statusCategory[props.category]]};
		${(props) =>
			props.isEditable
				? css({
						'&:hover': {
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							backgroundColor: `${bgHoverColor[statusCategory[props.category]]}`,
						},
					})
				: ''};
	}

	> span > span {
		text-overflow: clip;
		white-space: normal;
	}
`;

const LozengeOverrides = componentWithFG(
	'fly-2200-styled-component-migration',
	LozengeOverridesNew,
	LozengeOverridesOld,
);
